import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Package, X, Truck, Loader2, User, ArrowDownUp, RotateCcw, Check, AlertTriangle,
  MessageCircle, ShoppingCart, Undo2,
} from 'lucide-react';
import { api } from '../../../api/client';
import { EntityLink } from '../../../components/shared/EntityLink';
import { hiRes, ScanResult } from '../shared';
import { StatusBadge } from '../components/StatusBadge';
import { ConvertToInternalModal } from '../../../components/stock/ConvertToInternalModal';
import { Can, useCan } from '../../../lib/rbac';

/**
 * Devolución recibida por escaneo de su guía (2026-09-23).
 *
 * El operario escanea la guía de la devolución (DHL en México) y este pop-up muestra
 * la venta con su historial completo (venta → compra Amazon → envío → reclamo con
 * motivo → devolución → recepción) y las MISMAS dos acciones que una cancelada:
 * convertir a stock interno / pedir devolución en Amazon. El escaneo ya selló la
 * recepción en el backend; acá solo se muestra (y se puede deshacer si fue un error).
 */
export function ReturnScanModal({ result, sellerId, onClose }: {
  result: ScanResult; sellerId: number; onClose: () => void;
}) {
  const { t } = useTranslation();
  const can = useCan();
  const canMoney = can('global.view_money');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [undone, setUndone] = useState(false);
  const [convertModal, setConvertModal] = useState<{
    stockProductId: number; asin: string; title: string; unitCost: number; qtyOnHand: number; qty: number;
  } | null>(null);

  const ret = result.return || {};
  const claim = result.claim || {};
  const purchase = result.purchase;
  const orderSellerId = result.seller?.id || sellerId;
  const firstItem = result.items?.[0];
  const title: string = firstItem?.title || purchase?.asin || '';
  const thumb = hiRes(firstItem?.thumbnail);
  const timeline: Array<{ at: string | null; kind: string; text: string }> = result.timeline || [];

  const fmt = (d: string | null | undefined) =>
    d ? new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—';

  // Mismo circuito que "Convertir a stock interno" de la cancelada (ScanResultModal):
  // preparar la ficha de stock del producto y abrir el formulario de conversión.
  const handleConvert = async () => {
    if (!purchase?.asin) { setError(t('wh.return.noAsin')); return; }
    setBusy(true); setError(null);
    try {
      const lookup: any = await api.get(
        `/internal-stock/product-by-asin?asin=${purchase.asin}&seller_id=${orderSellerId}&create=1&title=${encodeURIComponent(title || purchase.asin)}`
      );
      if (!lookup.success || !lookup.data) throw new Error(lookup.error || t('wh.return.prepareFailed'));
      setConvertModal({
        stockProductId: lookup.data.product_id,
        asin: purchase.asin,
        title: lookup.data.title || title || purchase.asin,
        unitCost: purchase.unit_cost != null ? parseFloat(String(purchase.unit_cost)) : 0,
        qtyOnHand: lookup.data.qty_on_hand ?? 0,
        qty: Number(purchase.quantity) || 1,
      });
    } catch (err: any) {
      setError(err?.message || t('wh.return.prepareFailed'));
    } finally {
      setBusy(false);
    }
  };

  const handleUndoReceived = async () => {
    if (!ret.claim_db_id) return;
    setBusy(true); setError(null);
    try {
      const res: any = await api.post(`/warehouse/returns/${ret.claim_db_id}/received/undo`, { seller_id: sellerId });
      if (!res.success) throw new Error(res.error || t('wh.return.undoFailed'));
      setUndone(true);
    } catch (err: any) {
      setError(err?.message || t('wh.return.undoFailed'));
    } finally {
      setBusy(false);
    }
  };

  const KIND_ICON: Record<string, React.ReactNode> = {
    sale: <ShoppingCart size={13} />,
    amazon_purchase: <Package size={13} />,
    amazon_delivered: <Check size={13} />,
    ml_shipment: <Truck size={13} />,
    ml_delivered: <Check size={13} />,
    claim_opened: <AlertTriangle size={13} />,
    claim_messages: <MessageCircle size={13} />,
    claim_resolved: <Check size={13} />,
    return_created: <RotateCcw size={13} />,
    return_status: <RotateCcw size={13} />,
    return_received: <Check size={13} />,
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-2 sm:p-4" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[95vh] flex flex-col rounded-2xl shadow-2xl bg-violet-50 dark:bg-violet-950 ring-4 ring-violet-500"
        onClick={e => e.stopPropagation()}
      >
        {/* Cabecera */}
        <div className="shrink-0 flex items-center justify-between px-4 py-2.5 border-b border-violet-300 dark:border-violet-700">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="px-3 py-1 rounded-full text-sm font-semibold bg-violet-600 text-white">
              {t('wh.return.badge')}
            </span>
            {result.seller && <span className="text-lg font-bold text-[var(--color-text)]">{result.seller.nickname}</span>}
            {result.order && <StatusBadge status={result.order.status} />}
            {result.order?.context_site && (
              <span className="text-xs font-mono px-2 py-0.5 rounded bg-violet-200 dark:bg-violet-800 text-violet-900 dark:text-violet-100">
                {String(result.order.context_site)}
              </span>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 hover:bg-[var(--color-bg-hover)] rounded-lg">
            <X size={20} className="text-[var(--color-text-secondary)]" />
          </button>
        </div>

        {/* Cuerpo desplazable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {/* Recepción */}
          <div className={`p-3 rounded-xl border-2 ${undone
            ? 'border-gray-300 bg-gray-100 dark:bg-gray-800/40'
            : 'border-emerald-500 bg-emerald-100 dark:bg-emerald-900/30'}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div>
                <p className={`text-base font-bold ${undone ? 'text-gray-600 dark:text-gray-300' : 'text-emerald-800 dark:text-emerald-200'}`}>
                  {undone ? t('wh.return.receiptUndone') : ret.first_time_received ? t('wh.return.receivedNow') : t('wh.return.receivedBefore')}
                </p>
                {!undone && (
                  <p className="text-xs text-emerald-800/80 dark:text-emerald-300/80 mt-0.5">
                    {fmt(ret.received_at)}{ret.received_by ? ` · ${ret.received_by}` : ''}
                  </p>
                )}
                <p className="text-xs mt-1 text-[var(--color-text-secondary)]">
                  <span className="font-mono">{ret.tracking_number}</span>
                  {' · '}
                  {ret.live_verified ? t('wh.return.liveVerified') : t('wh.return.liveNotVerified')}
                </p>
              </div>
              {!undone && (
                <button
                  onClick={handleUndoReceived}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs rounded-lg border border-emerald-600 text-emerald-800 dark:text-emerald-200 hover:bg-emerald-200/60 dark:hover:bg-emerald-800/40 disabled:opacity-50"
                  title={t('wh.return.undoHint')}
                >
                  {busy ? <Loader2 size={12} className="animate-spin" /> : <Undo2 size={12} />}
                  {t('wh.return.undo')}
                </button>
              )}
            </div>
          </div>

          {/* Producto + venta */}
          <div className="bg-[var(--color-bg)] rounded-xl p-4 border border-[var(--color-border)]">
            <div className="flex gap-3">
              {thumb ? (
                <img src={thumb} alt="" className="w-20 h-20 rounded-lg object-cover bg-[var(--color-surface)] shrink-0" />
              ) : (
                <div className="w-20 h-20 rounded-lg bg-[var(--color-surface)] flex items-center justify-center shrink-0">
                  <Package size={28} className="text-[var(--color-text-muted)]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[var(--color-text)] line-clamp-2 leading-tight">{title || '—'}</p>
                <div className="flex items-center gap-1.5 mt-1.5 text-xs flex-wrap">
                  {result.order?.ml_order_id && (
                    <EntityLink type="ml_order" value={result.order.ml_order_id} sellerId={orderSellerId} />
                  )}
                  {purchase?.asin && <EntityLink type="asin" value={purchase.asin} sellerId={orderSellerId} />}
                  {purchase?.amazon_order_id && (
                    <EntityLink type="amazon_order" value={purchase.amazon_order_id} sellerId={orderSellerId} />
                  )}
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-xs text-[var(--color-text-secondary)] flex-wrap">
                  {result.order?.buyer_nickname && (
                    <span className="inline-flex items-center gap-1"><User size={11} />{result.order.buyer_nickname}</span>
                  )}
                  {purchase && (
                    <span>{purchase.quantity} {Number(purchase.quantity) === 1 ? t('wh.return.unit') : t('wh.return.units')}</span>
                  )}
                  {canMoney && purchase?.unit_cost != null && (
                    <span>{t('wh.cost')}: USD {Number(purchase.unit_cost).toFixed(2)}</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Motivo de la devolución */}
          <div className="rounded-xl p-4 border-2 border-orange-400 bg-orange-50 dark:bg-orange-900/20">
            <p className="text-xs uppercase tracking-wide font-semibold text-orange-700 dark:text-orange-300">
              {t('wh.return.reasonTitle')}
            </p>
            <p className="text-base font-bold text-[var(--color-text)] mt-1">
              {claim.problem || claim.title || claim.reason || claim.type || '—'}
            </p>
            {claim.detail && <p className="text-sm italic text-[var(--color-text-secondary)] mt-1">{claim.detail}</p>}
            <div className="flex items-center gap-3 mt-2 text-xs text-[var(--color-text-secondary)] flex-wrap">
              {claim.id && <span className="font-mono">#{claim.id}</span>}
              <span>{t('wh.return.claimStatus')}: <b>{claim.status}</b>{claim.stage ? ` / ${claim.stage}` : ''}</span>
              {claim.message_count > 0 && (
                <span className="inline-flex items-center gap-1"><MessageCircle size={11} />{claim.message_count}</span>
              )}
              <span>
                {t('wh.return.money')}: <b>{ret.status_money === 'refunded' ? t('wh.return.refunded') : ret.status_money === 'retained' ? t('wh.return.retained') : (ret.status_money || '—')}</b>
              </span>
              <span>{t('wh.return.returnStatus')}: <b>{ret.status || '—'}</b></span>
            </div>
          </div>

          {/* Historial */}
          <div className="bg-[var(--color-bg)] rounded-xl p-4 border border-[var(--color-border)]">
            <p className="text-xs uppercase tracking-wide font-semibold text-[var(--color-text-secondary)] mb-2">
              {t('wh.return.historyTitle')}
            </p>
            <ol className="space-y-1.5">
              {timeline.map((e, i) => (
                <li key={i} className="flex items-start gap-2 text-sm">
                  <span className={`mt-0.5 shrink-0 ${e.kind === 'return_received' ? 'text-emerald-600' : e.kind.startsWith('claim') ? 'text-orange-600' : 'text-[var(--color-text-muted)]'}`}>
                    {KIND_ICON[e.kind] || <Package size={13} />}
                  </span>
                  <span className="font-mono text-xs text-[var(--color-text-muted)] shrink-0 w-[7.5rem]">{fmt(e.at)}</span>
                  <span className="text-[var(--color-text)]">{e.text}</span>
                </li>
              ))}
              {timeline.length === 0 && <li className="text-sm text-[var(--color-text-muted)]">—</li>}
            </ol>
          </div>

          {error && (
            <div className="p-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 text-sm">{error}</div>
          )}
        </div>

        {/* Pie con las acciones (mismas que la cancelada) */}
        <div className="shrink-0 p-4 border-t-2 border-violet-400 bg-violet-100 dark:bg-violet-900/30">
          <div className="flex items-center justify-center gap-3 flex-wrap">
            {purchase?.asin ? (
              purchase.converted_to_stock ? (
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">
                  <ArrowDownUp size={14} />
                  {t('wh.alreadyConverted')}
                </span>
              ) : (
                <Can cap="warehouse.convert_internal">
                  <button
                    onClick={handleConvert}
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 text-white rounded-xl text-sm font-bold disabled:opacity-50"
                    title={t('wh.return.convertHint')}
                  >
                    {busy ? <Loader2 size={16} className="animate-spin" /> : <ArrowDownUp size={16} />}
                    {t('wh.convertToInternal')}
                  </button>
                </Can>
              )
            ) : (
              <span className="text-sm text-[var(--color-text-secondary)]">{t('wh.return.noPurchase')}</span>
            )}
            {purchase?.amazon_order_id && can('warehouse.request_amazon_cancel') && (
              <a
                href={`https://www.amazon.com/gp/your-account/order-details?orderID=${purchase.amazon_order_id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white rounded-xl text-sm font-bold"
                title={t('wh.return.amazonReturnHint')}
              >
                <Truck size={16} />
                {t('wh.return.requestAmazonReturn')}
              </a>
            )}
          </div>
        </div>
      </div>

      {convertModal && (
        <ConvertToInternalModal
          isOpen={true}
          onClose={() => { setConvertModal(null); onClose(); }}
          onSuccess={() => { setConvertModal(null); onClose(); }}
          sellerId={orderSellerId}
          stockProductId={convertModal.stockProductId}
          productTitle={convertModal.title}
          productAsin={convertModal.asin}
          qtyOnHand={convertModal.qtyOnHand}
          defaultQty={convertModal.qty}
          lockQty={true}
          defaultUnitCost={canMoney ? convertModal.unitCost : 0}
          source={{
            type: 'amazon_purchase',
            purchase_id: purchase?.id,
            ml_order_id: result.order?.ml_order_id ? String(result.order.ml_order_id) : undefined,
          } as any}
          defaultNotes={`Devolución recibida (guía ${ret.tracking_number ?? ''}) — venta #${result.order?.ml_order_id ?? ''} (escaneo WH)`}
        />
      )}
    </div>
  );
}
